/**
 * CC-49982 — report generation status copy.
 *
 * CB streams free-text progress through `process-status.lastMessage`. Some of
 * those strings read like hard failures ("...report is too large to
 * generate...") while the run is still working, and the report then completes
 * normally seconds later. Piping `lastMessage` straight into the spinner label
 * (and into the stall guard's thrown Error) told users the report had crashed
 * right before it rendered. Observed by an internal tester in the July 2026
 * beta and during the 7/9 QA walk on Origination & Production Compensation
 * (report 162, ~33k rows).
 *
 * Rule: while polling, never surface a failure-sounding CB string — show
 * progress. Only a terminal failure produces error copy, and a genuine
 * capacity failure has to say what to do next rather than read like a crash.
 */

// CB has no status code for "too big" — only prose. Match the phrasings the
// legacy proc/controller emit rather than one exact sentence.
const CAPACITY_RE = /too\s+large|too\s+many\s+rows|exceeds?\s+the\s+maximum/i

/** Does this CB status string describe a size/capacity condition? */
export const isCapacityMessage = (message) => CAPACITY_RE.test(String(message ?? ''))

/**
 * Label for the in-flight spinner. A capacity notice mid-run is progress, not
 * an error — the run is still alive, so say so.
 */
export const progressLabel = (message) => {
  const text = String(message ?? '').trim()
  if (!text) return 'Running report…'
  if (isCapacityMessage(text)) return 'Still working — this is a large report…'
  return text
}

/**
 * Copy for a run that actually failed. Capacity failures get the next step
 * (narrow the range / add filters) instead of CB's raw sentence.
 */
export const terminalErrorMessage = (message, fallback = 'Report generation failed') => {
  const text = String(message ?? '').trim()
  if (!text) return fallback
  if (isCapacityMessage(text)) {
    return 'This report is too large to generate with the current filters. Narrow the date range or add filters, then run it again.'
  }
  if (isTimeoutMessage(text)) return TIMEOUT_MESSAGE
  // CC-49993: everything else used to `return text` — CB's raw exception string,
  // straight into the red box. On QA that meant users read
  //   Access to the path 'E:\Storage\d_VladDemo\ReportInfo\<guid>' is denied.
  // — a server filesystem path, an internal volume layout and a tenant directory
  // name. cb-api already sanitizes its HTTP error paths (safeErrorResponse), but
  // this one rides a 200 in `lastMessage`, so it slipped past. Anything we don't
  // positively recognize is now generic copy; the raw text goes to the console for
  // support instead of to the user.
  if (isInternalDetail(text)) {    console.error('[report] CB reported a terminal failure:', text)
    return fallback
  }
  return text
}

export const TIMEOUT_MESSAGE =
  'This report took too long to return. Narrow the date range or add a filter, then run it again.'

// CB/Cloudflare/axios all phrase this differently; match the shapes, not a sentence.
const TIMEOUT_RE = /\btime(d)?\s*-?\s*out\b|timeout of \d+ms|ECONNABORTED|ETIMEDOUT|\b(504|524)\b/i

export const isTimeoutMessage = (message) => TIMEOUT_RE.test(String(message ?? ''))

/**
 * Does this string look like server internals rather than something a user should
 * read? Absolute paths, UNC shares, stack frames, SQL/.NET exception type names and
 * connection strings all qualify. Deliberately conservative in the other direction:
 * an unrecognized *plain* sentence still reaches the user, because CB does emit
 * genuinely useful prose ("No data for the selected period") and blanket-replacing
 * all of it would be a worse outcome than the leak.
 */
const INTERNAL_DETAIL_RE = new RegExp([
  '[A-Za-z]:\\\\',                       // E:\Storage\...
  '\\\\\\\\[A-Za-z0-9._-]+\\\\',         // \\server\share
  '\\bat\\s+[A-Za-z0-9_.]+\\([^)]*\\)',  // at Namespace.Method(...)
  '\\b\\w+Exception\\b',                 // SqlException, NullReferenceException
  '\\bStack\\s*Trace\\b',
  '\\b(Data\\s+Source|Initial\\s+Catalog|User\\s+ID)\\s*=',  // connection strings
  '/(?:var|etc|usr|home|opt)/',          // POSIX server paths
].join('|'), 'i')

export const isInternalDetail = (message) => INTERNAL_DETAIL_RE.test(String(message ?? ''))

/**
 * CC-49993 — sanitize an already-stringified error for display.
 *
 * The 19 specialized report pages each do `setError(err.message || 'Report failed')`
 * and hand the string to SpecializedReportShell, so there is no error object left to
 * inspect by then. Applying this at the shell's render point covers all of them at
 * once rather than editing nineteen catch blocks.
 */
export const userFacingError = (text, fallback = 'Report failed') => {
  const s = String(text ?? '').trim()
  if (!s) return ''
  if (isTimeoutMessage(s)) return TIMEOUT_MESSAGE
  if (isInternalDetail(s)) {    console.error('[report] error withheld from the user:', s)
    return fallback
  }
  return s
}

/**
 * CC-49993 — user-facing copy for a report run that threw.
 *
 * Two things were wrong before. A transport timeout reached the screen as axios's
 * own string, `timeout of 30000ms exceeded` — which is what QA reported as "it is
 * showing me timeout error". And the 5xx branch composed
 * `${err.message} (HTTP ${status})`, which re-opened the raw-detail leak for any
 * error whose message came from the server (including a Cloudflare 504/524 HTML
 * page). Both now resolve to copy that names the next action.
 *
 * Note this is about *reporting* the failure honestly. It does not stop the request
 * from being fired, and it must never truncate or cap a result — a report that is
 * genuinely too big is the server's answer to give, not ours to fake.
 */
export const runFailureMessage = (err, fallback = 'Report run failed') => {
  // A transport-level abort has no response at all.
  const code = err?.code
  if (code === 'ECONNABORTED' || code === 'ETIMEDOUT' || isTimeoutMessage(err?.message)) {
    return TIMEOUT_MESSAGE
  }
  const status = err?.response?.status
  if (status === 408 || status === 504 || status === 524) return TIMEOUT_MESSAGE

  // cb-api's own sanitized error envelope is safe to show as-is.
  const serverErr = err?.response?.data?.error || err?.response?.data?.message
  const detail = err?.response?.data?.detail
  if (serverErr && !isInternalDetail(serverErr)) {
    return detail && !isInternalDetail(detail) ? `${serverErr} — ${detail}` : serverErr
  }
  if (serverErr) {    console.error('[report] server error withheld from the user:', serverErr, detail || '')
    return fallback
  }

  const message = String(err?.message ?? '').trim()
  if (!message) return fallback
  if (isInternalDetail(message)) {    console.error('[report] error withheld from the user:', message)
    return fallback
  }
  // A 5xx with no usable body: say something true without echoing axios internals.
  if (status >= 500) return 'The report could not be generated. Please try again, or contact support if it keeps failing.'
  return message
}
